import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, ListPlus, Check, Loader2 } from 'lucide-react';

export const SaveToListModal = ({ open, onClose, adId }) => {
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(null);
    const [saved, setSaved] = useState({});

    useEffect(() => {
        if (open) {
            setLoading(true);
            api.get('/me/lists').then(({ data }) => {
                setLists(data);
                // mark already-saved
                const map = {};
                data.forEach(l => { if (l.items?.includes(adId)) map[l.id] = true; });
                setSaved(map);
            }).catch(() => {}).finally(() => setLoading(false));
        }
    }, [open, adId]);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        try {
            const { data } = await api.post('/me/lists', { name: newName.trim() });
            setLists(prev => [data, ...prev]);
            setNewName('');
            setCreating(false);
        } catch {
            toast.error('No se pudo crear la lista');
        }
    };

    const handleToggle = async (list) => {
        if (saving) return;
        setSaving(list.id);
        try {
            if (saved[list.id]) {
                await api.delete(`/me/lists/${list.id}/items/${adId}`);
                setSaved(prev => ({ ...prev, [list.id]: false }));
                toast.success(`Quitado de "${list.name}"`);
            } else {
                await api.post(`/me/lists/${list.id}/items`, { ad_id: adId });
                setSaved(prev => ({ ...prev, [list.id]: true }));
                toast.success(`Guardado en "${list.name}"`);
            }
        } catch {
            toast.error('Error al actualizar la lista');
        } finally {
            setSaving(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-sm rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ListPlus className="h-5 w-5" />
                        Guardar en lista
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : (
                    <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                        {lists.length === 0 && !creating && (
                            <p className="text-sm text-muted-foreground text-center py-4">No tienes listas aún</p>
                        )}
                        {lists.map(list => (
                            <button
                                key={list.id}
                                onClick={() => handleToggle(list)}
                                disabled={saving === list.id}
                                className="flex items-center justify-between px-3 py-2.5 rounded-xl border hover:bg-secondary transition-colors text-sm"
                            >
                                <span className="font-medium">{list.name}</span>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    {saving === list.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                                        saved[list.id] ? <Check className="h-3.5 w-3.5 text-green-600" /> : null}
                                    {list.items?.length || 0} productos
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {creating ? (
                    <div className="flex gap-2 mt-2">
                        <Input
                            placeholder="Nombre de la lista"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            autoFocus
                        />
                        <Button size="sm" onClick={handleCreate} className="bg-red-600 hover:bg-red-700 shrink-0">Crear</Button>
                        <Button size="sm" variant="ghost" onClick={() => setCreating(false)} className="shrink-0">×</Button>
                    </div>
                ) : (
                    <Button variant="outline" className="w-full mt-2" onClick={() => setCreating(true)}>
                        <Plus className="h-4 w-4 mr-1" />Nueva lista
                    </Button>
                )}
            </DialogContent>
        </Dialog>
    );
};
